import * as React from "react";
import { X } from "lucide-react";
import { Presence } from "@radix-ui/react-presence";

// Simple className join helper
function cx(...parts: Array<string | false | null | undefined>) {
    return parts.filter(Boolean).join(" ");
}

export type AlertVariant = "primary" | "secondary" | "success" | "danger" | "warning" | "info" | "light" | "dark";

export interface AlertProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
    /** Matches react-bootstrap's `variant` */
    variant?: AlertVariant;
    /** Matches react-bootstrap's `dismissible` */
    dismissible?: boolean;
    /** Matches react-bootstrap's `onClose` */
    onClose?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
    /** Matches react-bootstrap's `show` (controlled). If undefined, component is uncontrolled. */
    show?: boolean;
    /** Matches react-bootstrap's `closeLabel` */
    closeLabel?: string;
    /** Basic fade transition toggle (analogous to RB's Fade). */
    transition?: boolean;
}

const VARIANT_STYLES: Record<AlertVariant, React.CSSProperties> = {
    primary: { backgroundColor: "#dbeafe", color: "#1e3a8a", borderColor: "#93c5fd" },
    secondary: { backgroundColor: "#f1f5f9", color: "#0f172a", borderColor: "#cbd5e1" },
    success: { backgroundColor: "#dcfce7", color: "#166534", borderColor: "#86efac" },
    danger: { backgroundColor: "#fee2e2", color: "#991b1b", borderColor: "#fca5a5" },
    warning: { backgroundColor: "#fef3c7", color: "#854d0e", borderColor: "#fde047" },
    info: { backgroundColor: "#dbeafe", color: "#1e3a8a", borderColor: "#93c5fd" },
    light: { backgroundColor: "#ffffff", color: "#0f172a", borderColor: "#cbd5e1" },
    dark: { backgroundColor: "#0f172a", color: "#f8fafc", borderColor: "#1e293b" }
};

/**
 * Drop-in replacement for react-bootstrap's <Alert> using Radix primitives philosophy
 * - Accessible by default (role="alert", aria-live)
 * - Supports controlled/uncontrolled `show`
 * - Optional fade transition via @radix-ui/react-presence
 * - Subcomponents: <Alert.Heading> and <Alert.Link>
 */
export function Alert(props: AlertProps) {
    const {
        variant = "info",
        dismissible,
        onClose,
        show: showProp,
        closeLabel = "Close",
        transition = true,
        className,
        children,
        style,
        ...rest
    } = props;

    const uncontrolled = showProp === undefined;
    const [open, setOpen] = React.useState(true);
    const show = uncontrolled ? open : !!showProp;

    const handleClose = (e?: React.MouseEvent<HTMLButtonElement>) => {
        onClose?.(e);
        if (uncontrolled) setOpen(false);
    };

    const content = (
        <div
            role="alert"
            aria-live="polite"
            className={cx("relative w-full rounded-lg border p-4 shadow-sm mb-4 mt-2", className)}
            style={{ ...VARIANT_STYLES[variant], borderWidth: "1px", ...style }}
            {...rest}
        >
            <div className="min-w-0 break-words pr-10">{children}</div>
            {dismissible && (
                <button
                    type="button"
                    onClick={handleClose}
                    aria-label={closeLabel}
                    className={cx(
                        "absolute z-10 inline-flex h-6 w-6 items-center justify-center",
                        "rounded-md border border-transparent border-opacity-50 backdrop-blur-sm",
                        "hover:bg-black hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-black focus:ring-opacity-20 dark:focus:ring-white dark:focus:ring-opacity-20",
                        "flex-shrink-0"
                    )}
                    style={{ top: "0.5rem", right: "0.5rem", left: "auto" }}
                >
                    <X className="h-4 w-4" aria-hidden />
                </button>
            )}
        </div>
    );

    if (!transition) {
        return show ? content : null;
    }

    return (
        <Presence present={show}>
            {/* Basic fade/scale transition */}
            <div className={cx("[transition:opacity_.15s_ease,transform_.2s_ease]", show ? "opacity-100 scale-100" : "opacity-0 scale-95")}>
                {content}
            </div>
        </Presence>
    );
}

/** Heading subcomponent (mirrors `Alert.Heading`) */
Alert.Heading = function AlertHeading({ className, children, ...rest }: React.HTMLAttributes<HTMLHeadingElement>) {
    return (
        <h4 className={cx("mb-1 text-base font-semibold tracking-tight", className)} {...rest}>
            {children}
        </h4>
    );
};

/** Link subcomponent (mirrors `Alert.Link`) */
Alert.Link = function AlertLink({ className, children, ...rest }: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
    return (
        <a className={cx("font-medium underline underline-offset-4 hover:no-underline", className)} {...rest}>
            {children}
        </a>
    );
};
